import { type JSONConfig, TuneControlsKind, MeasurementKind, GroupKind } from '@/engine/app/interfaces.ts';
import * as defaultObjectModel from './_viewObjects.config.ts';
import * as defaultViewOptionsModel from './_viewOptions.config.ts';
import * as defaultViewOptionsGroups from './_viewOptionsGroup.config.ts';
import { viewPorts } from '@/surgeries/cardiac/TTVR/configs/views/_common.config.ts';
import { heartWorkflowIndications } from './_wfIndications.config.ts';
import { axial, coronal, sagittal } from '@/engine/common/imageSlices.config.ts';
import { SliceType } from '@/engine/app/modules/images/interfaces.ts';

export const config: JSONConfig.View = {
  descriptor: 'devicePlacement',
  label: 'Device',
  sideLabel: 'Device placement',
  imageStackDescriptor: 'Heart',
  imagesRoiConfig: {
    descriptor: 'basal_rv.roi',
    syncSliceDescriptor: SliceType.SAGITTAL,
    zoomFactor: 0.5
  },
  viewOrientationDescriptor: 'heart_orientation',
  viewOptions: [
    {
      ...defaultViewOptionsGroups.devices,
      children: [
        defaultViewOptionsModel.valve,
        defaultViewOptionsModel.catheter,
        defaultViewOptionsModel.additionalDevice,
        defaultViewOptionsModel.additionalStructure
      ]
    },
    {
      ...defaultViewOptionsGroups.rightHeart,
      children: [
        defaultViewOptionsModel.rightAtrium,
        defaultViewOptionsModel.rightVentricle,
        defaultViewOptionsModel.rightHeart,
        defaultViewOptionsModel.pulmonaryArtery,
        defaultViewOptionsModel.calcification
      ]
    },
    {
      ...defaultViewOptionsGroups.leftHeart,
      children: [
        defaultViewOptionsModel.leftAtrium,
        defaultViewOptionsModel.leftVentricle,
        defaultViewOptionsModel.aorta
      ]
    }
  ],
  objects: [
    { ...defaultObjectModel.landingZoneSpline, isVisible: true },
    { ...defaultObjectModel.valve, isVisible: true, isContourVisible: true },
    defaultObjectModel.catheter,
    defaultObjectModel.leftAtrium,
    defaultObjectModel.leftVentricle,
    defaultObjectModel.rightAtrium,
    defaultObjectModel.rightVentricle,
    defaultObjectModel.rightHeart,
    defaultObjectModel.aorta,
    defaultObjectModel.pulmonaryArtery,
    defaultObjectModel.eustachianRidgeSpline,
    defaultObjectModel.additionalDevice,
    defaultObjectModel.additionalStructure,
    defaultObjectModel.veinsCalcification,
    defaultObjectModel.calcification
  ],
  centerLines: [
    {
      descriptor: 'long_axis.centerline',
      isVisible: false
    }
  ],
  measurements: [
    {
      descriptor: 'landing_zone_area',
      isPickable: false,
      isTunable: false,
      isVisible: true
    },
    {
      descriptor: 'var_basal_rv_ap',
      isPickable: false,
      isTunable: false,
      isVisible: true,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_basal_rv_sl',
      isPickable: false,
      isTunable: false,
      isVisible: true,
      zoomFactor: 0.5
    },
    {
      descriptor: 'var_basal_rv_tv_ap',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_basal_rv_tv_sl',
      isPickable: false,
      isTunable: false,
      isVisible: false,
      zoomFactor: 0.7
    },
    {
      descriptor: 'var_annulus_oversize'
    },
    {
      descriptor: 'var_basal_rv_oversize'
    },
    {
      descriptor: 'tp.pl_svc1.area',
      isSelected: false,
      isPickable: false,
      isVisible: false
    },
    {
      descriptor: 'tp.pl_ivc1.area',
      isSelected: false,
      isPickable: false,
      isVisible: false
    },
    {
      descriptor: 'var_ra_height',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_rv_depth',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_rv_depth_apex',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_ivc_to_tva',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_ivc_to_tva_offset',
      isVisible: false,
      zoomFactor: 0.8
    },
    {
      descriptor: 'var_landing_zone_area_dP'
    }
  ],
  measurementBoxes: [
    {
      kind: MeasurementKind.Value,
      label: 'Anatomical Sizing',
      groupDescriptor: 'sizing',
      hasCriteria: true,
      order: 1,
      entries: ['var_annulus_oversize', 'var_basal_rv_oversize']
    },
    {
      kind: MeasurementKind.Value,
      label: 'Tricuspid annulus (projected)',
      groupDescriptor: 'sizing',
      order: 2,
      entries: ['var_landing_zone_area_dP']
    },
    {
      kind: MeasurementKind.Distance,
      groupDescriptor: 'sizing',
      label: 'Basal RV at 5 mm',
      order: 2,
      entries: ['var_basal_rv_ap', 'var_basal_rv_sl']
    }
  ],
  objectBoxes: [],
  workflowIndications: [...heartWorkflowIndications],
  orientationPresets: [],
  tuneControls: [
    {
      order: 1,
      kind: TuneControlsKind.Switch,
      groupDescriptor: 'sizing',
      label: 'AP Basal RV oversize calculation',
      descriptor: 'tp.is_basal_rv_size_ap_based'
    },
    {
      kind: TuneControlsKind.Reset,
      label: 'Reset size',
      groupDescriptor: 'sizing',
      descriptor: ['tp.valve']
    },
    {
      kind: TuneControlsKind.Select,
      label: 'Device manufacturer',
      descriptor: 'tp.manufacturer'
    },
    {
      kind: TuneControlsKind.Select,
      label: 'Valve system',
      descriptor: 'tp.system'
    },
    {
      kind: TuneControlsKind.Select,
      groupDescriptor: 'sizing',
      label: 'Valve size',
      descriptor: 'tp.valve'
    },
    {
      kind: TuneControlsKind.Reset,
      label: 'Reset device position',
      groupDescriptor: 'positioning',
      descriptor: ['tp.valveInsertionDepth', 'tp.valveApTranslation', 'tp.valveSlTranslation']
    },
    {
      kind: TuneControlsKind.Stepper,
      label: 'Device Insertion Depth',
      groupDescriptor: 'positioning',
      units: {
        value: 'mm'
      },
      descriptor: 'tp.valveInsertionDepth'
    },
    {
      kind: TuneControlsKind.Stepper,
      label: 'A/P Translation',
      groupDescriptor: 'positioning',
      units: {
        value: 'mm'
      },
      descriptor: 'tp.valveApTranslation'
    },
    {
      kind: TuneControlsKind.Stepper,
      label: 'S/L Translation',
      groupDescriptor: 'positioning',
      units: {
        value: 'mm'
      },
      descriptor: 'tp.valveSlTranslation'
    }
  ],
  imageSlices: [axial, coronal, sagittal],
  viewPorts,
  visibilityDependencies: [],
  selectionDependencies: [],
  helpers: [],
  planningEntitiesGroups: [
    {
      descriptor: 'sizing',
      label: 'Sizing',
      kind: GroupKind.Collapsible,
      isExpanded: true
    },
    {
      descriptor: 'positioning',
      label: 'Positioning',
      kind: GroupKind.Collapsible
    }
  ]
};
